import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { MenuItem } from "@progress/kendo-react-layout"
import { GridCellProps } from "@progress/kendo-react-grid";
import { routes } from "../../app/routes";
import { usePageActions } from "..";
import { PermissionDefinitions } from "../../security";
import { ProviderModel } from "../../data/entities";
import { ProviderService } from "../../services";
import { AddProviderModal, ManageProviderModal } from "./components";
import { TitlelessCard,
  ODataGrid,
  Breakpoints,
  ConfirmationModal,
  IconAndText,
  IconDefinitions,
  ResponsiveGridColumn,
  Icon,
  Button } from "../../components"
import { useCRUDHandlers,
  useGridActions,
  usePermission,
  useSetPageTitle,
  useSharedState,
  useTenantFeatures,
  useUrlParams } from "../../hooks";
import { createODataQueryFilter } from "../../utils";
import { useSelector } from "react-redux";
import { RootState } from "../../app";


export function LocationProvidersPage() {

  const location = useLocation();
  const gridData = useSharedState<Array<ProviderModel>>([]);
  const locationId = useUrlParams("id");
  const providerService = new ProviderService();
  const tenantId = useSelector((state: RootState) => state.authentication.tenantId);
  const { onClick, onSelect, showDelete,
    showAddState, showEditState, editItemState, showDeleteState, deleteItemState
    } = useGridActions<ProviderModel>(`${routes.location.provider.details}?returnUrl=${location.pathname}`, undefined, (dataItem) =>
      new Map([
        ["id", locationId],
        ["providerId", dataItem.id.toString()]
      ]));
  const { gridRef,
    getItems, addItem, editItem, deleteItem
    } = useCRUDHandlers<ProviderModel>(providerService, "Provider", showAddState, showEditState);

  const { manageProviders } = useTenantFeatures();
  const canAddProvider = usePermission(PermissionDefinitions.provider.create(tenantId!)) && manageProviders;
  const canDeleteProvider = usePermission(PermissionDefinitions.provider.delete(locationId!)) && manageProviders;

  useSetPageTitle("Providers", []);
  usePageActions(
    canAddProvider && (
      <Button
        onClick={() => {
          showAddState.setter(true);
        }}
      >
        <IconAndText iconName={IconDefinitions.add} text="Add" />
      </Button>
    )
  , [canAddProvider]);

  const commandsCell = useCallback(
    (props: GridCellProps) => (
      <td className="text-center">
        <Icon
          iconName={IconDefinitions.delete}
          className="text-danger"
          onClick={() => showDelete(props.dataItem)}
        />
      </td>
    ),
    [showDelete]
  );

  return (
    <TitlelessCard bodyClassName="p-0">
      <ODataGrid
        ref={gridRef}
        getData={(oDataQuery) => {
         const locationFilter = [`locationId eq ${locationId}`];
         const filterQuery = createODataQueryFilter(oDataQuery, locationFilter);
         return getItems(filterQuery)
        }}
        dataState={gridData}
        onRowClick={(e: any) => {
          onClick(e, new Map([
            ["id", locationId],
            ["providerId", e.dataItem.id]
          ]));
        }}
        onRowMenuSelect={onSelect}
        sort={[{ field: "name", dir: "asc"}]}
        scrollable="scrollable"
        pageSize={10}
      >
        <ResponsiveGridColumn
          title="Name"
          field="name"
          sortable={true}
          width={"auto"}
        />
        <ResponsiveGridColumn
          title="Description"
          field="description"
          sortable={true}
          filterable={false}
          width={"auto"}   
          breakpoint={Breakpoints.md}
        />
        <ResponsiveGridColumn
          title="Type"
          field="providerType"
          sortable={true}
          filterable={false}
          width={"auto"}
          breakpoint={Breakpoints.lg}
        />
        <ResponsiveGridColumn
          title="Capacity"
          field="capacity"
          sortable={true}
          filterable={false}
          width={"auto"}
          breakpoint={Breakpoints.xl}
        />
        <ResponsiveGridColumn
          title="Priority"
          field="priority"
          sortable={true}
          filterable={false}
          width={"auto"}
          breakpoint={Breakpoints.xl}
        />
        <ResponsiveGridColumn
          title="Active"
          field="isActive"
          sortable={true}
          filterable={false}
          width={"auto"}
          cell={(props) => <td>{props.dataItem.isActive ? "Yes" : "No"}</td>}
          breakpoint={Breakpoints.lg}
        />
        {canDeleteProvider === true && (
          <ResponsiveGridColumn
            className="column-url"
            field="url"
            title=" "
            sortable={false}
            filterable={false}
            headerCell={() => null}
            cell={commandsCell}
            width={50}
          />
        )}
        <MenuItem
          text="Open"
          data={{ action: "open" }}
          icon="hyperlink-open"
        />
        <MenuItem
          text="Open Tab"
          data={{ action: "opentab" }}
          icon="windows"
        />
        {canDeleteProvider && (
          <MenuItem
          text="Delete"
          data={{ action: "delete" }}
          icon={IconDefinitions.delete}
        />
        )}
      </ODataGrid>
        <AddProviderModal
          showState={showAddState}
          onSubmit={addItem}
        />
        <ManageProviderModal
          showState={showEditState}
          dataItemState={editItemState}
          onSubmit={editItem}
        />
        <ConfirmationModal
          showState={showDeleteState}
          title={`Delete Provider ${deleteItemState.value?.name}`}
          message={`You are about to delete this provider, it cannot be undone. Are you sure you want to delete ${deleteItemState.value.name}?`}
          confirmText="Delete"
          onConfirm={() => deleteItem(deleteItemState.value)}
        />
    </TitlelessCard>
  )
}

export default LocationProvidersPage
